import { AxiosError } from "axios";
import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Routes } from "@/config/page-view-constants";
import axioInstance from "@/libs/axios";
import { RootState } from "@/store/root-reducer";
import { MarketingConsent } from "@/types/MarketingConsent";
import { PaymentDetails } from "@/types/payment-details-types";
import { QuoteData } from "@/types/quote/quote-data";
import handleError, { ErrorResponse } from "@/utils/handle-errors";
import logError from "@/utils/logger";
import { normalizePostageOption } from "@/utils/review.utils";

type SaveRenewalReviewTypes = {
  marketingConsent: MarketingConsent;
  paymentDetails?: PaymentDetails | null;
  optOutAutoRenewal: boolean;
};

export default function useSaveRenewalReviewSubmit(resetTrigger: () => void) {
  const [data, setData] = useState<null | QuoteData>(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const { attributes, version, referenceId } = useSelector(
    (state: RootState) => state.quote
  );
  const router = useRouter();

  const saveRenewalReview = useCallback(
    async ({
      marketingConsent,
      paymentDetails,
      optOutAutoRenewal,
    }: SaveRenewalReviewTypes) => {
      const requestBody = {
        type: "Renewal",
        channel: "ONLINE",
        version,
        viewPage: Routes.QuoteReview,
        attributes: {
          domain: attributes.domain,
          marketingConsent,
          paymentDetails: {
            postageOption: normalizePostageOption(
              paymentDetails?.postageOption ??
                attributes?.paymentDetails?.postageOption
            ),
            optOutNextYearAutoRenewal:
              optOutAutoRenewal ??
              attributes?.paymentDetails?.optOutNextYearAutoRenewal,
          },
        },
      };

      const config = {
        data: requestBody,
      };

      try {
        const response = await axioInstance.patch(
          `/quote/${referenceId}/renewalReviewSubmit`,
          config
        );
        setData(response.data.data);
      } catch (err) {
        const axiosError = err as AxiosError;
        logError(axiosError, { location: "Review page save" });
        handleError(axiosError as AxiosError<ErrorResponse>, router, dispatch);
      } finally {
        setLoading(false);
        resetTrigger();
      }
    },
    [
      attributes.domain,
      attributes?.paymentDetails?.optOutNextYearAutoRenewal,
      attributes?.paymentDetails?.postageOption,
      dispatch,
      referenceId,
      resetTrigger,
      router,
      version,
    ]
  );

  return { data, loading, saveRenewalReview };
}
